import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileUp, RotateCcw, X } from 'lucide-react';
import { getErrorMessage } from '../../lib/apiClient';
import { formatBytes } from '../../utils/format';
import { DEFAULT_FILE_SIZE, FILE_TYPE_GROUPS, fileExtension } from './formModel';
import { uploadPublicFormFile } from './formsApi';

const button = 'shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100';

/**
 * One file for a form's file field. The file uploads as soon as it is chosen;
 * `value` is null or `{ status: 'uploading' | 'done' | 'error', name, size, progress, uploadToken, error }`,
 * and only a `done` value has a token to submit.
 */
export default function FormFileInput({ formId, field, pin, value, onChange }) {
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const controllerRef = useRef(null);
  const attemptRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState('');
  const allowed = field.allowedTypes ?? [];
  const maxBytes = field.maxFileSizeBytes ?? DEFAULT_FILE_SIZE;
  const kinds = FILE_TYPE_GROUPS.filter((group) => group.extensions.some((ext) => allowed.includes(ext))).map((group) => group.label.toLowerCase());

  useEffect(() => () => controllerRef.current?.abort(), []);

  const upload = async (file) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    const attempt = ++attemptRef.current;
    controllerRef.current = controller;
    fileRef.current = file;
    const base = { name: file.name, size: file.size };
    onChange({ ...base, status: 'uploading', progress: 0 });
    try {
      const uploadToken = await uploadPublicFormFile(formId, {
        file, fieldId: field.fieldId, pincode: pin, signal: controller.signal,
        onProgress: (progress) => { if (attempt === attemptRef.current) onChange({ ...base, status: 'uploading', progress }); },
      });
      if (attempt === attemptRef.current) onChange({ ...base, status: 'done', uploadToken });
    } catch (error) {
      if (attempt !== attemptRef.current || error?.name === 'AbortError') return;
      onChange({ ...base, status: 'error', error: getErrorMessage(error, 'Upload failed') });
    }
  };

  const choose = (file) => {
    if (!file) return;
    if (!allowed.includes(fileExtension(file.name))) return setRejected(`"${file.name}" is not an accepted file type.`);
    if (!file.size) return setRejected(`"${file.name}" is empty.`);
    if (file.size > maxBytes) return setRejected(`"${file.name}" is larger than ${formatBytes(maxBytes)}.`);
    setRejected('');
    upload(file);
  };

  const remove = () => {
    attemptRef.current += 1;
    controllerRef.current?.abort();
    fileRef.current = null;
    setRejected('');
    onChange(null);
  };

  const picker = <input ref={inputRef} type="file" className="hidden" aria-label={field.fieldLabel} accept={allowed.map((ext) => `.${ext}`).join(',')} onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />;
  const browse = (text) => <button type="button" className={button} onClick={() => inputRef.current?.click()}>{text}</button>;
  const accepts = kinds.join(', ').replace(/, ([^,]*)$/, ' or $1') || 'files';
  const hint = <p className="mt-1 text-xs text-slate-500">{accepts[0].toUpperCase() + accepts.slice(1)}, up to {formatBytes(maxBytes)}</p>;
  const error = rejected && <p role="alert" className="mt-2 text-sm text-red-600">{rejected}</p>;

  if (!value) return <div className="mt-2">
    <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={(e) => { e.preventDefault(); setDragOver(false); choose(e.dataTransfer.files?.[0]); }} className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${dragOver ? 'border-brand-500 bg-brand-50' : 'border-slate-300 bg-slate-50'}`}>
      <FileUp className="h-7 w-7 text-slate-400" /><p className="text-sm text-slate-600">Drag a file here, or</p>{browse('Browse files')}{hint}
    </div>{picker}{error}
  </div>;

  const uploading = value.status === 'uploading';
  const failed = value.status === 'error';
  return <div className="mt-2">
    <div className={`rounded-xl border px-4 py-3 ${failed ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex items-center gap-3">
        {value.status === 'done' ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" /> : <FileUp className="h-5 w-5 shrink-0 text-slate-400" />}
        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-800" title={value.name}>{value.name}</span><span className="text-xs text-slate-500">{uploading ? `Uploading… ${value.progress ?? 0}%` : failed ? value.error : formatBytes(value.size)}</span></span>
        {failed && fileRef.current && <button type="button" className={button} onClick={() => upload(fileRef.current)}><RotateCcw className="mr-1 inline h-4 w-4" />Retry</button>}
        {value.status === 'done' && browse('Replace')}
        <button type="button" aria-label={uploading ? 'Cancel upload' : 'Remove file'} className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-200" onClick={remove}><X className="h-4 w-4" /></button>
      </div>
      {uploading && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={value.progress ?? 0} aria-valuemin={0} aria-valuemax={100}><div className="h-full bg-brand-500 transition-all" style={{ width: `${value.progress ?? 0}%` }} /></div>}
    </div>{picker}{error}
  </div>;
}
